import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronUp, MessageCircle, User, Sparkles, ExternalLink, Star, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import IdeaCommentsSheet from "./IdeaCommentsSheet";
import BoostIdeaDialog from "@/components/payments/BoostIdeaDialog";
import { formatDistanceToNow } from "date-fns";
import SaveButton from "@/components/saved/SaveButton";

interface IdeaCardProps {
  idea: any;
  hasVoted: boolean;
  onVote: (hasVoted: boolean) => void;
  currentUserId: string | null;
  isGrantReady?: boolean;
}

const IdeaCard = ({ idea, hasVoted, onVote, currentUserId, isGrantReady }: IdeaCardProps) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const navigate = useNavigate();
  const profile = idea.profiles;
  const isExternal = idea.source_type === "external" && !!idea.source_url;
  let externalHost: string | null = null;
  if (isExternal) {
    try {
      externalHost = new URL(idea.source_url).host.replace(/^www\./, "");
    } catch {
      externalHost = idea.source_label || null;
    }
  }

  const handleVote = () => {
    if (!currentUserId) {
      navigate("/auth");
      return;
    }
    onVote(hasVoted);
  };

  const handleComments = () => {
    if (!currentUserId) {
      navigate("/auth");
      return;
    }
    setCommentsOpen(true);
  };

  return (
    <>
      <div className="break-inside-avoid mb-4 group">
        <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors">
          {idea.image_url && (
            <div className="relative overflow-hidden">
              <img
                src={idea.image_url}
                alt={idea.title}
                className="w-full object-cover max-h-72"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}

          <div className="p-4">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {idea.is_featured && (
                <Badge className="text-xs gap-1 bg-primary text-primary-foreground hover:bg-primary/90">
                  <Star className="h-3 w-3" /> Editor's Pick
                </Badge>
              )}
              {idea.boosted_until && new Date(idea.boosted_until) > new Date() && (
                <Badge className="text-xs gap-1 bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30">
                  <Zap className="h-3 w-3" /> Boosted
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs font-medium">
                {idea.category}
              </Badge>
              {isExternal && (
                <Badge variant="outline" className="text-xs gap-1 border-primary/30 text-primary">
                  <ExternalLink className="h-3 w-3" />
                  {idea.source_label || externalHost || "External"}
                </Badge>
              )}
              {idea.idea_type && (
                <Badge variant="outline" className="text-xs capitalize">
                  {idea.idea_type}
                </Badge>
              )}
              {idea.status === "draft" && (
                <Badge variant="outline" className="text-xs border-dashed text-muted-foreground">
                  Draft
                </Badge>
              )}
              {isGrantReady && (
                <Badge className="gap-1 text-xs bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                  <Sparkles className="h-3 w-3" /> Grant-Ready
                </Badge>
              )}
              {idea.tags?.slice(0, 2).map((tag: string) => (
                <Link
                  key={tag}
                  to={`/ideas/tag/${encodeURIComponent(tag)}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Badge variant="outline" className="text-xs hover:border-primary/60 hover:text-primary transition-colors cursor-pointer">
                    #{tag}
                  </Badge>
                </Link>
              ))}
            </div>

            {isExternal ? (
              <a
                href={idea.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block font-semibold text-foreground leading-snug mb-1.5 hover:text-primary transition-colors"
              >
                {idea.title}
              </a>
            ) : (
              <Link
                to={`/ideas/${idea.id}`}
                className="block font-semibold text-foreground leading-snug mb-1.5 hover:text-primary transition-colors"
              >
                {idea.title}
              </Link>
            )}
            {idea.description && (
              <p className="text-sm text-muted-foreground line-clamp-3">{idea.description}</p>
            )}

            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              {idea.user_id ? (
                <Link
                  to={`/profile/${idea.user_id}`}
                  className="flex items-center gap-2 min-w-0 flex-1 hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} className="h-6 w-6 rounded-full object-cover" alt="" />
                    ) : (
                      <User className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground truncate hover:text-primary transition-colors">
                    {profile?.display_name || "Anonymous"}
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <User className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground truncate">Anonymous</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground ml-auto shrink-0">
                {formatDistanceToNow(new Date(idea.created_at), { addSuffix: true })}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleVote}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  hasVoted
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                <ChevronUp className={`h-4 w-4 ${hasVoted ? "text-primary" : ""}`} />
                {idea.vote_count}
              </button>
              <button
                onClick={handleComments}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground bg-secondary hover:bg-secondary/80 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Comments
              </button>
              <SaveButton
                size="xs"
                item={{
                  item_type: "idea",
                  item_id: idea.id,
                  title: idea.title,
                  url: `/ideas/${idea.id}`,
                  source: idea.category ?? "Ideas",
                  cover_image_url: idea.image_url ?? null,
                  summary: idea.description ?? undefined,
                  metadata: { tags: idea.tags, idea_type: idea.idea_type },
                }}
              />
              {currentUserId && idea.user_id === currentUserId && (
                <button
                  onClick={() => setBoostOpen(true)}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 transition-colors"
                >
                  <Zap className="h-4 w-4" /> Boost
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {currentUserId && (
        <IdeaCommentsSheet
          open={commentsOpen}
          onOpenChange={setCommentsOpen}
          ideaId={idea.id}
          ideaTitle={idea.title}
          currentUserId={currentUserId}
        />
      )}
      {currentUserId && idea.user_id === currentUserId && (
        <BoostIdeaDialog
          open={boostOpen}
          onOpenChange={setBoostOpen}
          ideaId={idea.id}
          ideaTitle={idea.title}
        />
      )}
    </>
  );
};

export default IdeaCard;
